<template>
  <div>
      <!--  WELCOME -->
      <div v-if="$route.params.currentStep === onboardingSteps.WELCOME" class="app-page__container">
        <h3 class="title is-3 has-text-centered">
         <span role="img">👋</span>&nbsp;&nbsp;Welcome to StandupPro!
        </h3>
        <div class="columns u-m-t-large">
          <div class="column has-vertically-aligned-content">
            <p class="has-text-centered is-size-5 has-text-weight-bold u-m-b">Team Members</p>
            <div
              class="card card-content has-text-centered new-user__card"
              @click="handleGoToExistingWorkspace"
              @keyup.enter="handleGoToExistingWorkspace"
            >
              <b-icon icon="email-plus" custom-size="mdi-48px" />
              <p class="u-m-t">I'm here to join an existing workspace</p>
            </div>
          </div>
          <div class="column">
            <p class="has-text-centered is-size-5 has-text-weight-bold u-m-b">Workspace Owners</p>
            <div
              class="card card-content has-text-centered new-user__card"
              @click="handleGoToCreateWorkspace"
              @keyup.enter="handleGoToCreateWorkspace"
            >
              <b-icon icon="account-multiple-plus" custom-size="mdi-48px" />
              <p class="u-m-t">I'd like to create a new workspace</p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="hero is-transparent is-fullheight">
        <div class="hero-body container app-page__container">
          <!-- ACCEPT INVITATION -->
          <div v-if="$route.params.currentStep === onboardingSteps.ACCEPT_INVITATION" class="columns">
            <div class="column has-vertically-aligned-content">
              <h3 class="title is-3">
                You're invited to join {{ activeInvitation.workspaceName }}
              </h3>
            </div>
            <onboarding-complete-profile-form
              submitButtonText="Go To My Workspace"
              @submitSuccess="handleSubmitJoinWorkspace"
              :activeInvitationId="activeInvitationId"
            />
          </div>
          <!-- Create/Name Your Workspace -->
          <div v-if="$route.params.currentStep === onboardingSteps.CREATE_WORKSPACE" class="columns">
            <div class="column has-vertically-aligned-content">
              <h3 class="title is-3">
                Let's get you set up.
              </h3>
            </div>
            <div class="column">
              <onboarding-create-workspace-form @submitSuccess="handleSubmitCreateWorkspace" />
            </div>
          </div>

          <!-- Complete Your Profile -->
          <div v-else-if="$route.params.currentStep === onboardingSteps.COMPLETE_YOUR_PROFILE" class="columns">
            <div class="column has-vertically-aligned-content">
              <h3 class="title is-3">
                Welcome to StandupPro!
              </h3>
            </div>
            <div class="column">
              <onboarding-complete-profile-form @submitSuccess="handleSubmitCompleteProfile" />
            </div>
          </div>

          <!-- Create First Team -->
          <div v-else-if="$route.params.currentStep === onboardingSteps.CREATE_TEAM" class="columns">
            <div class="column">
              <onboarding-create-team-form  @submitSuccess="handleSubmitCreateTeam" />
            </div>
            <div class="column has-text-centered has-vertically-aligned-content is-hidden-mobile">
              <p>
                "Coming together is a beginning.
                <br>
                Keeping together is progress.
                <br>
                Working together is success."
                <br>
                <span class="is-italic has-text-weight-bold">- Henry Ford</span>
              </p>
            </div>
          </div>

          <!-- Invite Team Members -->
          <div v-else-if="$route.params.currentStep === onboardingSteps.INVITE_TEAM_MEMBERS" class="columns">
            <div class="column">
              <onboarding-invite-team-members-form
                :activeTeamId="activeTeamId"
                @submitSuccess="handleSubmitInviteTeammates"
                @skipStep="handleSkipInviteTeammates"
              />
            </div>
            <div class="column has-text-centered has-vertically-aligned-content is-hidden-mobile">
              <p>
                "It takes two flints to make a fire."
                <br>
                <span class="is-italic has-text-weight-bold">- Louisa May Alcott</span>
              </p>
            </div>
          </div>

          <!-- Schedule Daily Standup -->
          <div v-else-if="$route.params.currentStep === onboardingSteps.SCHEDULE_DAILY_STANDUP" class="columns">
            <div class="column is-6">
              <onboarding-schedule-daily-standup-form
                :activeTeamId="activeTeamId"
                @submitSuccess="handleSubmitCustomStandup"
                @skipStep="handleSkipCustomizeStandup"
              />
            </div>
            <div class="column has-text-centered has-vertically-aligned-content is-hidden-mobile">
              <p>
                "Everyday makes you ordinary.
                <br>
                Every single day makes you extraordinary.""
                <br>
                <span class="is-italic has-text-weight-bold">- Unknown</span>
              </p>
            </div>
          </div>

        </div>
      </div>
    <!--<b-loading is-full-page :active.sync="showLoading" :can-cancel="false"></b-loading>-->
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import * as InvitationActions from '@/store/actions/Invitation.actions';
// import { isObjectEmpty } from '@/helpers/dataHelpers';
import OnboardingCompleteProfileForm from '@/components/OnboardingCompleteProfileForm.vue';
import OnboardingCreateWorkspaceForm from '@/components/OnboardingCreateWorkspaceForm.vue';
import OnboardingCreateTeamForm from '@/components/OnboardingCreateTeamForm.vue';
import OnboardingInviteTeamMembersForm from '@/components/OnboardingInviteTeamMembersForm.vue';
import OnboardingScheduleDailyStandupForm from '@/components/OnboardingScheduleDailyStandupForm.vue';

export default {
  name: 'OnboardingWorkspaceOwnerView',

  components: {
    OnboardingCompleteProfileForm,
    OnboardingCreateWorkspaceForm,
    OnboardingCreateTeamForm,
    OnboardingInviteTeamMembersForm,
    OnboardingScheduleDailyStandupForm,
  },

  data() {
    return {
      onboardingSteps: {
        WELCOME: 'welcome',
        ACCEPT_INVITATION: 'accept-invitation',
        COMPLETE_YOUR_PROFILE: 'complete-your-profile',
        CREATE_WORKSPACE: 'create-workspace',
        CREATE_TEAM: 'create-team',
        INVITE_TEAM_MEMBERS: 'invite-team-members',
        SCHEDULE_DAILY_STANDUP: 'schedule-daily-standup',
      },
      activeTeamId: '', // Id of team created during onboarding. To be used for invites.
      isAcceptInvitationModalActive: false,
      activeInvitationId: '',
    };
  },

  computed: {
    ...mapGetters([
      'loggedInUser',
      'activeWorkspace',
    ]),
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      invitations: state => state.invitation.invitations,
      authUser: state => state.auth.authUser,
    }),
    activeInvitation() {
      return this.invitations[this.activeInvitationId];
    },
  },

  created() {
    if (
      !this.$route.params.currentStep
      || !Object.values(this.onboardingSteps).includes(this.$route.params.currentStep)
    ) {
      this.$router.push(`/onboarding/${this.onboardingSteps.WELCOME}`);
    }
  },

  methods: {
    handleSubmitCompleteProfile() {
      this.$router.push(`/onboarding/${this.onboardingSteps.CREATE_TEAM}`);
    },
    handleSubmitCreateWorkspace() {
      this.$router.push(`/onboarding/${this.onboardingSteps.COMPLETE_YOUR_PROFILE}`);
    },
    handleSubmitCreateTeam(createdTeamId) {
      this.activeTeamId = createdTeamId;
      this.$router.push(`/onboarding/${this.onboardingSteps.INVITE_TEAM_MEMBERS}`);
    },
    handleSubmitInviteTeammates() {
      this.$router.push(`/onboarding/${this.onboardingSteps.SCHEDULE_DAILY_STANDUP}`);
    },
    handleSkipInviteTeammates() {
      this.$router.push(`/onboarding/${this.onboardingSteps.SCHEDULE_DAILY_STANDUP}`);
    },
    handleSubmitCustomStandup() {
      this.$router.push('/dashboard');
    },
    handleSkipCustomizeStandup() {
      this.$router.push('/dashboard');
    },
    handleSubmitJoinWorkspace() {
      this.$router.push('/dashboard');
    },
    handleGoToCreateWorkspace() {
      this.isAcceptInvitationModalActive = false;
      this.$router.push(`/onboarding/${this.onboardingSteps.CREATE_WORKSPACE}`);
    },
    handleGoToExistingWorkspace() {
      this.getMostRecentUserInvitation();
    },
    handleAcceptInvitation() {
      this.$router.push('/dashboard');
    },
    getMostRecentUserInvitation() {
      this.$store.dispatch(InvitationActions.FETCH_PENDING_USER_INVITATIONS, { userEmail: this.authUser.email })
        .then(() => {
          const userInvitation = Object.values(this.invitations).find(invitation => invitation.inviteeEmail === this.authUser.email); // eslint-disable-line max-len
          if (userInvitation) {
            this.activeInvitationId = userInvitation.id;
            this.$router.push(this.onboardingSteps.ACCEPT_INVITATION);
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.hero-body {
  width: 100%;
}

.columns {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.new-user__card {
  cursor: pointer;
  transition: all 0.1s ease;
  color: $info;
  // border: 2px solid $info;
  // border-radius: 30px;
  font-weight: 600;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 7px 14px rgba(50,50,93,.08), 0 3px 6px rgba(0,0,0,.06);
  }
}
</style>
